import React, { useEffect, useRef, useState } from 'react';
import { API_URL } from './api';
import './BefidCustomerIdentityPanel.css';
import BefidIdentityPairing from './BefidIdentityPairing';
import BefidCustomerPhoneChange from './BefidCustomerPhoneChange';
import BefidRecoveryCode from './BefidRecoveryCode';

const FRESH_PROOF_CODES = new Set(['BEFID_IDENTITY_PROOF_REQUIRED', 'BEFID_FRESH_PROOF_REQUIRED', 'IDENTITY_VERIFICATION_REQUIRED']);
const LINK_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function readStatus(payload) {
  if (!payload || typeof payload.linked !== 'boolean' || typeof payload.sessionMatches !== 'boolean'
      || (payload.identity !== null && (!payload.identity || typeof payload.identity.id !== 'string'
        || typeof payload.identity.displayName !== 'string'))) throw new Error('BEFID_INVALID_STATUS');
  // The link generation stays in this component's memory, never in the DOM or storage.
  return { linked: payload.linked, sessionMatches: payload.sessionMatches, hasIdentity: payload.identity !== null,
    displayName: payload.identity?.displayName || '',
    linkId: payload.linked && LINK_ID_PATTERN.test(payload.linkId || '') ? payload.linkId : null };
}

function IdentityPanel({ slug, token, onReverify, identityBase, requestHeaders, onStatusChange, compactManagement, children }) {
  const base = identityBase || `${API_URL}/api/click-collect/${encodeURIComponent(slug)}/identity/befid`;
  const [status, setStatus] = useState(null);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState('status');
  const [error, setError] = useState(null);
  const [revision, setRevision] = useState(0);
  const [pairingMode, setPairingMode] = useState('');
  const [confirmUnlink, setConfirmUnlink] = useState(false);
  const [phoneChangeOpen, setPhoneChangeOpen] = useState(false);
  const [recoveryCodeOpen, setRecoveryCodeOpen] = useState(false);
  const [phoneChangeRestricted, setPhoneChangeRestricted] = useState(false);
  const [unlinkedNotice, setUnlinkedNotice] = useState(false);
  const confirmationRef = useRef(null);
  const unlinkButtonRef = useRef(null);
  const requestRef = useRef(null);
  const activeRef = useRef(false);

  const acceptStatus = payload => {
    const nextStatus = readStatus(payload);
    setStatus(nextStatus);
    const { linkId, ...publicStatus } = nextStatus;
    onStatusChange?.(publicStatus);
    return nextStatus;
  };

  const request = async (action) => {
    if (!activeRef.current || requestRef.current) return;
    if (action === 'unlink' && (!confirmUnlink || !status?.linked || !status.linkId)) return;
    const controller = new AbortController();
    requestRef.current = controller;
    setBusy(action);
    setError(null);
    if (action !== 'status') setUnlinkedNotice(false);
    onStatusChange?.(null);
    const refresh = async () => {
      const response = await fetch(base, {
        method: 'GET', credentials: 'include', cache: 'no-store', signal: controller.signal,
        headers: { ...requestHeaders, Authorization: `Bearer ${token}` },
      });
      const payload = await response.json().catch(() => null);
      if (!activeRef.current || controller.signal.aborted) return;
      if (!response.ok) throw new Error('BEFID_STATUS_UNAVAILABLE');
      acceptStatus(payload);
    };
    try {
      const response = await fetch(`${base}${['logout', 'unlink'].includes(action) ? `/${action}` : ''}`, {
        method: action === 'status' ? 'GET' : 'POST',
        credentials: 'include', cache: 'no-store', signal: controller.signal,
        headers: { ...requestHeaders, Authorization: `Bearer ${token}`, ...(action === 'status' ? {} : { 'Content-Type': 'application/json' }) },
        ...(action === 'status' ? {} : { body: JSON.stringify(action === 'connect' ? { consent: true }
          : action === 'unlink' ? { confirmation: true, expectedLinkId: status.linkId } : {}) }),
      });
      const payload = await response.json().catch(() => null);
      if (!activeRef.current || controller.signal.aborted) return;
      if (!response.ok) {
        if (action === 'unlink' && response.status === 409) {
          setConfirmUnlink(false);
          setStatus(null);
          await refresh();
          if (!activeRef.current || controller.signal.aborted) return;
          setError({ unlinkConflict: true, message: 'La liaison a changé. Son état a été actualisé. Vérifiez-le avant de demander à nouveau son détachement.' });
          return;
        }
        const reverify = FRESH_PROOF_CODES.has(payload?.code) || (action === 'unlink' && response.status === 401);
        setError({ reverify, conflict: response.status === 409 && !reverify, message: reverify
          ? 'Vérifiez de nouveau votre téléphone avant de modifier la liaison à votre identité BEFID.'
          : response.status === 409
            ? 'Cette identité ne peut pas être reliée pour le moment. Aucun rapprochement n’a été effectué.'
            : compactManagement ? 'Votre identité BEFID est momentanément indisponible. Réessayez avant de confirmer.' : 'Votre identité BEFID est momentanément indisponible. Vous pouvez continuer votre commande.' });
        return;
      }
      if (action === 'logout') {
        if (payload?.loggedOut !== true) throw new Error('BEFID_INVALID_STATUS');
        setStatus(null);
        onStatusChange?.(null);
        await refresh();
      } else {
        if (action === 'unlink' && (payload?.unlinked !== true || payload.linked !== false
            || payload.sessionMatches !== false || payload.identity !== null || payload.linkId !== null)) throw new Error('BEFID_INVALID_STATUS');
        acceptStatus(payload);
      }
      if (!activeRef.current || controller.signal.aborted) return;
      setConsent(false);
      setConfirmUnlink(false);
      if (action === 'unlink') setUnlinkedNotice(true);
    } catch (_) {
      if (activeRef.current && !controller.signal.aborted) {
        setError({ reverify: false, message: compactManagement ? 'Votre identité BEFID n’a pas pu être vérifiée. Réessayez avant de confirmer.' : 'Impossible de vérifier votre identité BEFID. Vous pouvez continuer votre commande puis réessayer.' });
      }
    } finally {
      if (requestRef.current === controller) requestRef.current = null;
      if (activeRef.current && !controller.signal.aborted) setBusy('');
    }
  };

  useEffect(() => { if (confirmUnlink) confirmationRef.current?.focus(); }, [confirmUnlink]);

  useEffect(() => {
    activeRef.current = true;
    request('status');
    return () => {
      activeRef.current = false;
      requestRef.current?.abort();
      requestRef.current = null;
    };
    // IdentityPanel is remounted whenever the verified restaurant/token changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revision]);

  const accountControls = status && (!error || error.unlinkConflict) && !pairingMode && !phoneChangeOpen && !recoveryCodeOpen && !phoneChangeRestricted && <>
      <p role="status">{status.sessionMatches && status.linked ? 'Votre identité BEFID est reliée à ce restaurant et connectée sur ce navigateur.'
        : status.linked ? 'Votre identité BEFID est reliée à ce restaurant. Elle n’est pas connectée sur ce navigateur.'
          : 'Votre compte de ce restaurant n’est pas encore relié à votre identité BEFID.'}</p>
      {!confirmUnlink && (status.sessionMatches && status.linked
        ? <><button type="button" disabled={Boolean(busy)} onClick={() => setPairingMode('authorize')}>Autoriser un autre site</button><button type="button" disabled={Boolean(busy)} onClick={() => request('logout')}>{busy === 'logout' ? 'Déconnexion…' : 'Déconnecter BEFID de cet appareil'}</button><p className="befid-customer-identity-note">La déconnexion ferme uniquement cette session BEFID. Votre compte et vos avantages au restaurant sont conservés.</p></>
        : <><button type="button" disabled={Boolean(busy)} onClick={() => { setConsent(false); setPairingMode('receive'); }}>Utiliser mon identité BEFID d’un autre site</button>
          {!status.linked && !status.hasIdentity && <p>Vous avez déjà une identité BEFID ailleurs ? Récupérez-la avec le bouton ci-dessus. La connexion ci-dessous crée une nouvelle identité si ce restaurant n’est pas encore relié.</p>}
          {status.hasIdentity && !status.linked && <><p>Session BEFID : {status.displayName || 'Identité sans nom'}</p><p>Continuez uniquement si cette identité est la vôtre. Sinon, déconnectez BEFID.</p></>}
          <label className="befid-customer-identity-consent"><input type="checkbox" checked={consent} disabled={Boolean(busy)} onChange={event => setConsent(event.target.checked)} /><span>J’accepte de relier mon compte de ce restaurant à mon identité BEFID et de la connecter sur ce navigateur. Cela ne modifie pas mes consentements marketing.</span></label>
          {(status.linked || status.hasIdentity) && <>{!status.hasIdentity && <p>Si une autre session BEFID est ouverte sur cet appareil, déconnectez-la avant de continuer.</p>}<button type="button" disabled={Boolean(busy)} onClick={() => request('logout')}>Déconnecter BEFID de cet appareil</button></>}
          <button type="button" disabled={!consent || Boolean(busy)} onClick={() => { if (consent) request('connect'); }}>{busy === 'connect' ? 'Connexion…' : status.hasIdentity && !status.linked ? 'Utiliser cette session BEFID' : 'Relier et connecter mon identité BEFID'}</button></>)}
      {status.linked && status.linkId && !confirmUnlink && <button className="befid-identity-secondary" type="button" disabled={Boolean(busy)} onClick={() => { setError(null); setPhoneChangeOpen(true); }}>Changer mon numéro de téléphone</button>}
      {status.linked && status.linkId && !confirmUnlink && <button className="befid-identity-secondary" type="button" disabled={Boolean(busy)} onClick={() => { setError(null); setRecoveryCodeOpen(true); }}>Préparer mon code de secours</button>}
      {status.linked && status.linkId && !confirmUnlink && <button ref={unlinkButtonRef} className="befid-identity-secondary" type="button" disabled={Boolean(busy)} onClick={() => { setError(null); setConfirmUnlink(true); }}>Détacher ce restaurant de BEFID</button>}
      {confirmUnlink && <div className="befid-identity-unlink" role="group" aria-label="Confirmer le détachement de ce restaurant" tabIndex={-1} ref={confirmationRef}>
        <h4>Détacher ce restaurant ?</h4>
        <p>Seule la liaison de ce restaurant à votre identité BEFID sera retirée. Votre fiche client, vos points, cadeaux, codes promo, achats et consentements restent conservés dans ce restaurant. Les liaisons avec les autres restaurants sont conservées.</p>
        <p>La session BEFID de ce navigateur sera fermée. Vous restez connecté à votre compte local du restaurant. Vous pourrez choisir de le relier à nouveau après vérification de votre téléphone.</p>
        <button type="button" disabled={Boolean(busy)} onClick={() => request('unlink')}>{busy === 'unlink' ? 'Détachement…' : 'Confirmer le détachement'}</button>
        <button className="befid-identity-secondary" type="button" disabled={Boolean(busy)} onClick={() => { setConfirmUnlink(false); setTimeout(() => unlinkButtonRef.current?.focus(), 0); }}>Conserver la liaison</button>
      </div>}
    </>;

  return <section className="befid-customer-identity" aria-label="Mon identité BEFID" aria-busy={Boolean(busy)}>
    {!compactManagement && <><h3>Mon identité BEFID</h3>
    <p>Retrouvez votre identité entre les établissements participants. Pour un autre site, autorisez explicitement la liaison depuis votre session BEFID existante. Vos points, cadeaux et codes promo restent propres à chaque restaurant.</p></>}
    {busy === 'status' && <p role="status">Vérification de votre identité BEFID…</p>}
    {unlinkedNotice && <p role="status">Ce restaurant est détaché de votre identité BEFID. Votre fiche et vos avantages locaux sont conservés.</p>}
    {error && <div role="alert"><p>{error.message}</p>
      {error.reverify && <button type="button" disabled={Boolean(busy)} onClick={onReverify}>Vérifier de nouveau mon téléphone</button>}
      {error.conflict && <button type="button" disabled={Boolean(busy)} onClick={() => request('logout')}>Déconnecter BEFID de cet appareil</button>}
      <button type="button" disabled={Boolean(busy)} onClick={() => setRevision(value => value + 1)}>Réessayer</button>
    </div>}
    {pairingMode && <BefidIdentityPairing slug={slug} token={token} identityBase={base} requestHeaders={requestHeaders} mode={pairingMode} onReverify={onReverify}
      onLinked={payload => { acceptStatus(payload); setConsent(false); setPairingMode(''); request('status'); }}
      onRefresh={() => { setPairingMode(''); setRevision(value => value + 1); }}
      onClose={() => { setPairingMode(''); setConsent(false); setRevision(value => value + 1); }} />}
    {recoveryCodeOpen && <BefidRecoveryCode token={token} expectedLinkId={status?.linkId} identityBase={base} requestHeaders={requestHeaders} onReverify={onReverify} onClose={() => setRecoveryCodeOpen(false)} />}
    {phoneChangeOpen && <BefidCustomerPhoneChange token={token} expectedLinkId={status?.linkId} identityBase={base} requestHeaders={requestHeaders} onReverify={onReverify}
      onStatusChange={() => { setPhoneChangeRestricted(true); onStatusChange?.(null); }}
      onClose={() => setPhoneChangeOpen(false)} />}
    {!phoneChangeOpen && phoneChangeRestricted && <div role="status"><p>Le suivi du changement de numéro a été fermé. Toute demande déjà confirmée continue d’être traitée. Reconnectez-vous avec votre nouveau numéro une fois le changement terminé ; si le changement est interrompu, contactez l’assistance.</p><button type="button" onClick={onReverify}>Me reconnecter avec mon nouveau numéro</button></div>}
    {compactManagement && status?.linked && status?.sessionMatches && !error && !pairingMode && !phoneChangeOpen && !recoveryCodeOpen && !phoneChangeRestricted
      ? <>
        <p className="befid-identity-ready" role="status">Votre identité BEFID est prête.</p>
        {!confirmUnlink && children}
        <details className="befid-identity-management">
          <summary>Gérer mon identité BEFID</summary>
          {accountControls}
        </details>
      </>
      : <>{accountControls}{!pairingMode && !phoneChangeOpen && !recoveryCodeOpen && !phoneChangeRestricted && children}</>}

  </section>;
}

export default function BefidCustomerIdentityPanel({ slug, token, onReverify, identityBase, requestHeaders = {}, onStatusChange, compactManagement = false, children }) {
  if (!slug || !token) return null;
  return <IdentityPanel key={`${slug}\u0000${token}\u0000${identityBase || ""}\u0000${JSON.stringify(requestHeaders)}`} slug={slug} token={token} onReverify={onReverify} identityBase={identityBase} requestHeaders={requestHeaders} onStatusChange={onStatusChange} compactManagement={compactManagement}>{children}</IdentityPanel>;
}
